//Libraries
import { FC } from 'react';
import { Grid } from '@mui/material';

//Providers
import { useAuth } from 'src/contexts';
import { useReferences } from 'src/services';

//Interfaces && Types
import { Draft } from 'src/types/common';
import { IAddress } from 'src/types/address';
import { IOrgModel } from 'src/types/org/orgInterface';
import { IPhone, getGradeOptions,instLvlOptions } from 'src/types';

//Processing
import { ChangeManager } from 'src/generic/utils/form';
import { IFieldErrors, ErrorManager } from 'src/types/validation';

//Components
import { AddressFields, formatAddress } from 'src/generic/component/address';
import {FieldRow, FieldLabel, Panel, Title, FieldNote, SubFieldLabel} from 'src/generic/component';
import {Input, Checkbox, BooleanRadio, Dropdown, TextArea, Checkboxes} from 'src/generic/component/form';
import {Typography} from "../../../components";

const TYPE_OPTIONS = [{key: 5, label: 'Classroom'}, {key: 4, label: 'Internet'}];
export interface IOrgFieldProps {
  readOnly?: boolean;
  type: 'ai' |'di';
  showProtected?: boolean;
  item: Draft<IOrgModel>;
  validating?: boolean;
  errors: IFieldErrors;
  original?: Draft<IOrgModel>;
  defaults?: Draft<IOrgModel>;
  hints?: any;
  showDiff?: boolean;
  onUpdate?: (address: Draft<IOrgModel>) => void;
}

const isPhoneEmpty = (phone: Draft<IPhone>) => {
  if (!phone.phoneCc && !phone.phoneLocal && !phone.phoneNdc) {
    return true;
  }
  return false;
};

const formatPhone = (phone: Draft<IPhone>) => {
  let formatted = "";
  if (phone.phoneCc) {
    formatted += `+${phone.phoneCc}`;
  }

  if (phone.phoneNdc) {
    formatted += `${phone.phoneNdc}`;
  }

  if (phone.phoneLocal) {
    formatted += `${phone.phoneLocal}`;
  }
  return formatted;
};

export const OrgFields: FC<IOrgFieldProps> = ({ showProtected, original, defaults, hints, item: initial, readOnly, type,errors, onUpdate }) => {
  const { userName } = useAuth();
  const { types, subTypes,schoolSubTypes, schoolLevels } = useReferences();

  /**
     * Form Variables and Monitoring Functionality
     *
     * This is the place where most additions and modifications will occur for the form
     * in addition to the rendering block.
     */
  const {
    name,
    nameShort,
    nameAbbr,
    nameShortAbbr,
    nameNick,
    nameFormerOfficial,
    nameRoot,
    website,
    phone,
    fax,
    email,
    mdrPid,
    ncesId,
    feeWaiverId,
    ipedsId,
    campus,
    charterInd,
    lowestGradeCd,
    highestGradeCd,
    orgTypeCd,
    orgSubTypeCd,
    instLvlCd,
    schoolSubTypeCd,
    schoolLevelCd,
    orgCodeNotes
  } = initial || {};

  const deliveryMethod: number[] = initial.educationDeliveryMethodCds || [];
  const visitingAddress: Draft<IAddress> = initial?.visitingAddress || {countryIsoCd: 'US'};
  const shippingAddress: Draft<IAddress> | undefined = initial?.shippingAddress;

  const originalShipping: Draft<IAddress> | undefined = original?.shippingAddress;

  const gradeOptions = getGradeOptions().map((item) => ({ key: item.value, label: item.label }));
  const instLvlOptionsAll = instLvlOptions.map((item) => ({ key: item.value, label: item.label }));

  const getGradeLabel = (key: string | number) => {
    const parsed = (typeof key === 'string') ? parseInt(key) : key;
    const found = gradeOptions.find((item) => {
      const compare = typeof item.key === 'string' ? parseInt(item.key) : item.key;
      return compare === parsed;
    });
    return found?.label;
  };

  const { getError, getFieldError, hasError } = ErrorManager(errors);

  const changed = (updates: Draft<IOrgModel>) => {
    if (onUpdate) {
      onUpdate({ ...initial, ...updates });
    }
  };
  const { isChanged, getChangeColor, getCurrent, format, formatBoolean } = ChangeManager<IOrgModel>(original || {}, initial);

  return <>
    <Panel>
      <Title>Institution Information</Title>
      <Grid container spacing={5}>
        <Grid item xs={6}>
          <FieldRow>
              <FieldLabel variant="body1">Institution Name</FieldLabel>
              {(original && isChanged('name')) &&
                  <div><i className={getChangeColor('name')}>Current: {getCurrent('name') || '(empty)'}</i></div>
              }
              <Input
                  className={original ? getChangeColor('name') : ''}
                  disabled={readOnly} value={name} validation={hasError('name') ? 'error' : undefined}
                  validationMsg={getError('name')}
                  onChange={(e) => {
                      changed({name: e.target.value || undefined});
                  }}
              />
              {(name || '').length > 0 &&
                  <FieldNote>Character Count: {(name || '').length}</FieldNote>
              }
          </FieldRow>
            <FieldRow>
                <FieldLabel variant="body1">Institution Short Name</FieldLabel>
                {(original && isChanged('nameShort')) &&
                    <div><i className={getChangeColor('nameShort')}>Current: {getCurrent('nameShort') || '(empty)'}</i>
                    </div>
                }
                <Input
                    className={original ? getChangeColor('nameShort') : ''}
                    disabled={readOnly} value={nameShort} validation={hasError('nameShort') ? 'error' : undefined}
                    validationMsg={getError('nameShort')}
                    onChange={(e) => {
                        changed({nameShort: e.target.value || undefined});
                    }}
                />
                {(nameShort || '').length > 0 &&
                    <FieldNote>Character Count: {(nameShort || '').length}</FieldNote>
                }
            </FieldRow>
            <FieldRow>
                <FieldLabel variant="body1">Institution Abbreviated Name</FieldLabel>
                {(original && isChanged('nameAbbr')) &&
                    <div><i className={getChangeColor('nameAbbr')}>Current: {getCurrent('nameAbbr') || '(empty)'}</i>
                    </div>
                }
                <Input
                    className={original ? getChangeColor('nameAbbr') : ''}
                    disabled={readOnly} value={nameAbbr} validation={hasError('nameAbbr') ? 'error' : undefined}
                    validationMsg={getError('nameAbbr')}
                    onChange={(e) => {
                        changed({nameAbbr: e.target.value || undefined});
                    }}
                />
                {(nameAbbr || '').length > 0 &&
                    <FieldNote>Character Count: {(nameAbbr || '').length}</FieldNote>
                }
            </FieldRow>
        </Grid>
      {showProtected &&
        <Grid item xs={6}>
             <FieldRow>
             <FieldLabel variant="body1">Institution Short Abbreviated Name</FieldLabel>
             {(original && isChanged('nameShortAbbr')) &&
                 <div><i
                     className={getChangeColor('nameShortAbbr')}>Current: {getCurrent('nameShortAbbr') || '(empty)'}</i>
                 </div>
             }
             <Input
                 className={original ? getChangeColor('nameShortAbbr') : ''}
                 disabled={readOnly} value={nameShortAbbr}
                 validation={hasError('nameShortAbbr') ? 'error' : undefined}
                 validationMsg={getError('nameShortAbbr')}
                 onChange={(e) => {
                     changed({nameShortAbbr: e.target.value || undefined});
                 }}
             />
             {(nameShortAbbr || '').length > 0 &&
                 <FieldNote>Character Count: {(nameShortAbbr || '').length}</FieldNote>
             }
         </FieldRow>
            <FieldRow>
                <FieldLabel variant="body1">Institution Nickname</FieldLabel>
                {(original && isChanged('nameNick')) &&
                    <div><i className={getChangeColor('nameNick')}>Current: {getCurrent('nameNick') || '(empty)'}</i>
                    </div>
                }
                <Input
                    className={original ? getChangeColor('nameNick') : ''}
                    disabled={readOnly} value={nameNick} validation={hasError('nameNick') ? 'error' : undefined}
                    validationMsg={getError('nameNick')}
                    onChange={(e) => {
                        changed({nameNick: e.target.value || undefined});
                    }}
                />
                {(nameNick || '').length > 0 &&
                    <FieldNote>Character Count: {(nameNick || '').length}</FieldNote>
                }
            </FieldRow>
            <FieldRow>
                <FieldLabel variant="body1">Institution Former Official Name</FieldLabel>
                {(original && isChanged('nameFormerOfficial')) &&
                    <div><i
                        className={getChangeColor('nameFormerOfficial')}>Current: {getCurrent('nameFormerOfficial') || '(empty)'}</i>
                    </div>
                }
                <Input
                    className={original ? getChangeColor('nameFormerOfficial') : ''}
                    disabled={readOnly} value={nameFormerOfficial}
                    validation={hasError('nameFormerOfficial') ? 'error' : undefined}
                    validationMsg={getError('nameFormerOfficial')}
                    onChange={(e) => {
                        changed({nameFormerOfficial: e.target.value || undefined});
                    }}
                />
                {(nameFormerOfficial || '').length > 0 &&
                    <FieldNote>Character Count: {(nameFormerOfficial || '').length}</FieldNote>
                }
            </FieldRow>
            <FieldRow>
                <FieldLabel variant="body1">Institution Root Name</FieldLabel>
                {(original && isChanged('nameRoot')) &&
                    <div><i className={getChangeColor('nameRoot')}>Current: {getCurrent('nameRoot') || '(empty)'}</i>
                    </div>
                }
                <Input
                    className={original ? getChangeColor('nameRoot') : ''}
                    disabled={readOnly} value={nameRoot} validation={hasError('nameRoot') ? 'error' : undefined}
                    validationMsg={getError('nameRoot')}
                    onChange={(e) => {
                        changed({nameRoot: e.target.value || undefined});
                    }}
                />
                {(nameRoot || '').length > 0 &&
                    <FieldNote>Character Count: {(nameRoot || '').length}</FieldNote>
                }
            </FieldRow>
        </Grid>
      }
      </Grid>
      <Grid container spacing={5}>
        <Grid item xs={6}>
          <FieldRow>
            <FieldLabel>Institution Phone</FieldLabel>
            <FieldNote>User Submitted: {hints.phone}</FieldNote>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <SubFieldLabel>Country Code</SubFieldLabel>
                {(original && isChanged('phone.phoneCc')) &&
                  <div><i className={getChangeColor('phone.phoneCc')}>Current: {getCurrent('phone.phoneCc') || '(empty)'}</i></div>
                }
                <Input
                  className={original ? getChangeColor('phone.phoneCc') : ''}
                  disabled={readOnly} validation={hasError('phone.phoneCc') ? 'error' : undefined} validationMsg={getError('phone.phoneCc')}
                  onChange={(e) => {
                    const updated = {
                      ...phone, phoneCc: e.target.value || undefined
                    }
                    changed({
                      phone: {
                        ...updated,
                        formatted: formatPhone(updated)
                      }
                    });
                  }}
                  value={phone?.phoneCc || ''}
                />
              </Grid>
              <Grid item xs={3}>
                <SubFieldLabel>Phone Ndc</SubFieldLabel>
                {(original && isChanged('phone.phoneNdc')) &&
                  <div><i className={getChangeColor('phone.phoneNdc')}>Current: {getCurrent('phone.phoneNdc') || '(empty)'}</i></div>
                }
                <Input
                  className={original ? getChangeColor('phone.phoneNdc') : ''}
                  disabled={readOnly} validation={hasError('phone.phoneNdc') ? 'error' : undefined} validationMsg={getError('phone.phoneNdc')}
                  onChange={(e) => {
                    const updated = {
                      ...phone, phoneNdc: e.target.value || undefined
                    }
                    changed({
                      phone: {
                        ...updated,
                        formatted: formatPhone(updated)
                      }
                    });
                  }}
                  value={phone?.phoneNdc || ''}
                />
              </Grid>
              <Grid item xs={6}>
                <SubFieldLabel>Phone Local</SubFieldLabel>
                {(original && isChanged('phone.phoneLocal')) &&
                  <div><i className={getChangeColor('phone.phoneLocal')}>Current: {getCurrent('phone.phoneLocal') || '(empty)'}</i></div>
                }
                <Input
                  className={original ? getChangeColor('phone.phoneLocal') : ''}
                  disabled={readOnly} validation={hasError('phone.phoneLocal') ? 'error' : undefined} validationMsg={getError('phone.phoneLocal')}
                  onChange={(e) => {
                    const updated = {
                      ...phone, phoneLocal: e.target.value || undefined
                    }
                    changed({
                      phone: {
                        ...updated,
                        formatted: formatPhone(updated)
                      }
                    });
                  }}
                  value={phone?.phoneLocal || ''}
                />
              </Grid>
            </Grid>
          </FieldRow>
          <FieldRow>
            <FieldLabel>Institution Fax</FieldLabel>
            <FieldNote>User Submitted: {hints.fax}</FieldNote>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <SubFieldLabel>Country Code</SubFieldLabel>
                {(original && isChanged('fax.phoneCc')) &&
                    <div><i className={getChangeColor('fax.phoneCc')}>Current: {getCurrent('fax.phoneCc') || '(empty)'}</i></div>
                }
                <Input
                    className={original ? getChangeColor('fax.phoneCc') : ''}
                    disabled={readOnly} validation={hasError('fax.phoneCc') ? 'error' : undefined} validationMsg={getError('fax.phoneCc')}
                    onChange={(e) => {
                      const updatedFax = {
                        ...fax,
                        phoneCc: e.target.value || undefined,
                      };
                      const isEmpty = isPhoneEmpty(updatedFax);
                      changed({
                        fax: isEmpty
                          ? undefined
                          : {
                              ...updatedFax,
                              formatted: formatPhone(updatedFax),
                            },
                      });
                    }}
                    value={fax?.phoneCc || ''}
                />
              </Grid>
              <Grid item xs={3}>
                <SubFieldLabel>Fax Ndc</SubFieldLabel>
                {(original && isChanged('fax.phoneNdc')) &&
                    <div><i className={getChangeColor('fax.phoneNdc')}>Current: {getCurrent('fax.phoneNdc') || '(empty)'}</i></div>
                }
                <Input
                    className={original ? getChangeColor('fax.phoneNdc') : ''}
                    disabled={readOnly} validation={hasError('fax.phoneNdc') ? 'error' : undefined} validationMsg={getError('fax.phoneNdc')}
                    onChange={(e) => {
                      const updatedFax = {
                        ...fax,
                        phoneNdc: e.target.value || undefined,
                      };
                      const isEmpty = isPhoneEmpty(updatedFax);
                      changed({
                        fax: isEmpty
                          ? undefined
                          : {
                              ...updatedFax,
                              formatted: formatPhone(updatedFax),
                            },
                      });
                    }}
                    value={fax?.phoneNdc || ''}
                />
              </Grid>
              <Grid item xs={6}>
                <SubFieldLabel>Fax Local</SubFieldLabel>
                {(original && isChanged('fax.phoneLocal')) &&
                    <div><i className={getChangeColor('fax.phoneLocal')}>Current: {getCurrent('fax.phoneLocal') || '(empty)'}</i></div>
                }
                <Input
                    className={original ? getChangeColor('fax.phoneLocal') : ''}
                    disabled={readOnly} validation={hasError('fax.phoneLocal') ? 'error' : undefined} validationMsg={getError('fax.phoneLocal')}
                    onChange={(e) => {
                      const updatedFax = {
                        ...fax,
                        phoneLocal: e.target.value || undefined,
                      };
                      const isEmpty = isPhoneEmpty(updatedFax);
                      changed({
                        fax: isEmpty
                          ? undefined
                          : {
                              ...updatedFax,
                              formatted: formatPhone(updatedFax),
                            },
                      });
                    }}
                    value={fax?.phoneLocal || ''}
                />
              </Grid>
            </Grid>
          </FieldRow>

          <FieldRow>
            <FieldLabel variant="body1">General Institution Website</FieldLabel>
            {(original && isChanged('website')) &&
              <div><i className={getChangeColor('website')}>Current: {getCurrent('website') || '(empty)'}</i></div>
            }
            <Input
              className={original ? getChangeColor('website') : ''}
              disabled={readOnly} validation={hasError('website') ? 'error' : undefined} validationMsg={getError('website')}
              onChange={(e) => { changed({ website: e.target.value || undefined }); }}
              value={website || ''}
            />
          </FieldRow>
          <FieldRow>
            <FieldLabel variant="body1">General Institution Email</FieldLabel>
            {(original && isChanged('email')) &&
              <div><i className={getChangeColor('email')}>Current: {getCurrent('email') || '(empty)'}</i></div>
            }
            <Input
              className={original ? getChangeColor('email') : ''}
              disabled={readOnly} validation={hasError('email') ? 'error' : undefined} validationMsg={getError('email')}
              onChange={(e) => { changed({ email: e.target.value || undefined }); }}
              value={email || ''}
            />
          </FieldRow>
        </Grid>
        <Grid item xs={6}>
          <FieldRow>
            <FieldLabel variant="body1">Organization Type</FieldLabel>
            {(hints?.orgTypeCd) &&
              <FieldNote>User Submitted: {hints?.orgTypeCd.join(', ')}</FieldNote>
            }
            {(original && isChanged('orgTypeCd')) &&
              <div><i className={getChangeColor('orgTypeCd')}>Current: {types.find((item) => item.key == getCurrent('orgTypeCd'))?.label || '(empty)'}</i></div>
            }
            <Dropdown
              className={original ? getChangeColor('orgTypeCd') : ''}
              disabled={readOnly} validation={getError('orgTypeCd') ? 'error' : undefined} validationMsg={getError('orgTypeCd')}
              values={types} onChange={(e) => {
                const value = parseInt(e.target.value);
                const code = isNaN(value) ? undefined : value;
                const requireGrade = [1, 9].includes(code || 0);
                const change: any = {
                  orgTypeCd: code,
                  orgSubTypeCd: undefined,
                  charterInd: requireGrade ? (charterInd || defaults?.charterInd) : undefined
                };
                change.charterInd = requireGrade ? (charterInd || defaults?.charterInd) : undefined;
                change.campus = requireGrade ? (campus || defaults?.campus) : undefined;
                change.lowestGradeCd = requireGrade ? (lowestGradeCd || defaults?.lowestGradeCd) : undefined;
                change.highestGradeCd = requireGrade ? (highestGradeCd || defaults?.highestGradeCd) : undefined;
                change.schoolSubTypeCd = requireGrade ? (schoolSubTypeCd  || defaults?.schoolSubTypeCd) : undefined;
                change.schoolLevelCd = requireGrade ? (schoolLevelCd || defaults?.schoolLevelCd) : undefined;
                changed(change);
              }} value={orgTypeCd?.toString()}></Dropdown>
          </FieldRow>

          <FieldRow>
            <FieldLabel variant="body1">Organization Sub-Type</FieldLabel>
            {(original && isChanged('orgSubTypeCd')) &&
              <div><i className={getChangeColor('orgSubTypeCd')}>Current: {subTypes.find((item) => item.key == getCurrent('orgSubTypeCd'))?.label || '(empty)'}</i></div>
            }
            <Dropdown
              className={original ? getChangeColor('orgSubTypeCd') : ''}
              disabled={readOnly} validation={getError('orgSubTypeCd') ? 'error' : undefined} validationMsg={getError('orgSubTypeCd')}
              values={subTypes.filter((item) => item.parentId === orgTypeCd || !item.parentId)} onChange={(e) => {
                const value = parseInt(e.target.value);
                changed({
                  orgSubTypeCd: isNaN(value) ? undefined : value
                });
              }} value={orgSubTypeCd?.toString()}></Dropdown>
          </FieldRow>
          { type == 'di' && 
          <>
            <FieldRow>
            <FieldLabel variant="body1">Institution Level</FieldLabel>
            {(original && isChanged('instLvlCd')) &&
              <div><i className={getChangeColor('instLvlCd')}>Current: {instLvlOptionsAll.find((item) => item.key == getCurrent('instLvlCd'))?.label || '(empty)'}</i></div>
            }
            <Dropdown
              disabled={readOnly}
              values={instLvlOptionsAll}
              validation={getError('instLvlCd') ? 'error' : undefined}
              validationMsg={getError('instLvlCd')}
              onChange={(e) => {
                 const value = parseInt(e.target.value);
                changed({instLvlCd: isNaN(value) ? undefined : value});
            }}
              value={instLvlCd?.toString()}//load existing data
            />
          </FieldRow>
          </>
          }

            {showProtected &&
                <>
                    <FieldRow>
                        <FieldLabel variant="body1">School Sub-Type</FieldLabel>
                        {(original && isChanged('schoolSubTypeCd')) &&
                            <div><i
                                className={getChangeColor('schoolSubTypeCd')}>Current: {schoolSubTypes.find((item) => item.key == getCurrent('schoolSubTypeCd'))?.label || '(empty)'}</i>
                            </div>
                        }
                        {/* TODO: Convert to label from key */}
                        <Dropdown
                            className={original ? getChangeColor('schoolSubTypeCd') : ''}
                            disabled={readOnly} validation={getError('schoolSubTypeCd') ? 'error' : undefined}
                            validationMsg={getError('schoolSubTypeCd')}
                            values={schoolSubTypes}
                            onChange={(e) => {
                                const value = parseInt(e.target.value);
                                changed({schoolSubTypeCd: isNaN(value) ? undefined : value});
                            }}
                            value={schoolSubTypeCd?.toString()}>
                        </Dropdown>
                    </FieldRow>
                    <FieldRow>
                        <FieldLabel variant="body1">School Level</FieldLabel>
                    {(original && isChanged('schoolLevelCd')) &&
                            <div><i className={getChangeColor('schoolLevelCd')}>Current: {schoolLevels.find((item) => item.key == getCurrent('schoolLevelCd'))?.label || '(empty)'}</i></div>
                    }
                    {/* TODO: Convert to label from key */}
                    <Dropdown
                        className={original ? getChangeColor('schoolLevelCd') : ''}
                        disabled={readOnly} validation={getError('schoolLevelCd') ? 'error' : undefined} validationMsg={getError('schoolLevelCd')}
                        values={schoolLevels}
                        onChange={(e) => {
                            const value = parseInt(e.target.value);
                            changed({ schoolLevelCd: isNaN(value) ? undefined : value });
                        }} value={schoolLevelCd?.toString()}></Dropdown>
                </FieldRow>
                </>
            }

          {orgTypeCd === 1 &&
            <>
              <FieldRow>
                <FieldLabel variant="body1">Charter Indicator</FieldLabel>
                {/* {(original && isChanged('charterInd')) &&
                  <div><i className={getChangeColor('charterInd')}>Current: {getCurrent('charterInd') || '(empty)'}</i></div>
                } */}
                <BooleanRadio name='charterInd' readOnly={readOnly} value={charterInd} error={getError('charterInd')}
                  onChange={(value) => {
                    changed({ charterInd: value });
                  }} />
              </FieldRow>
            </>
          }

          {[1, 9].includes(orgTypeCd || 0) &&
            <>
                <FieldRow>
                    <FieldLabel variant="body1">Campus</FieldLabel>
                    {/* {(original && isChanged('charterInd')) &&
              <div><i className={getChangeColor('charterInd')}>Current: {getCurrent('charterInd') || '(empty)'}</i></div>
            } */}
                    <BooleanRadio name='campus' readOnly={readOnly} value={campus} error={getError('campus')}
                                  onChange={(value) => {
                                      changed({campus: value});
                                  }}/>
                </FieldRow>
                <FieldRow>
                    <FieldLabel variant="body1">Lowest Grade Offered</FieldLabel>
                    {(original && isChanged('lowestGradeCd')) &&
                        <div><i
                            className={getChangeColor('lowestGradeCd')}>Current: {getGradeLabel(getCurrent('lowestGradeCd')) || '(empty)'}</i>
                        </div>
                    }
                    {/* TODO: Convert to label from key */}
                    <Dropdown
                        className={original ? getChangeColor('lowestGradeCd') : ''}
                        disabled={readOnly} validation={getError('lowestGradeCd') ? 'error' : undefined}
                        validationMsg={getError('lowestGradeCd')}
                        values={gradeOptions}
                        onChange={(e) => {
                            const value = parseInt(e.target.value);
                            changed({lowestGradeCd: isNaN(value) ? undefined : value});
                        }}
                        value={lowestGradeCd?.toString()}>
                    </Dropdown>
                </FieldRow>
                <FieldRow>
                    <FieldLabel variant="body1">Highest Grade Offered</FieldLabel>
                    {(original && isChanged('highestGradeCd')) &&
                        <div><i
                            className={getChangeColor('highestGradeCd')}>Current: {getGradeLabel(getCurrent('highestGradeCd')) || '(empty)'}</i>
                        </div>
                    }
                    {/* TODO: Convert to label from key */}
                    <Dropdown
                        className={original ? getChangeColor('highestGradeCd') : ''}
                        disabled={readOnly} validation={getError('highestGradeCd') ? 'error' : undefined}
                        validationMsg={getError('highestGradeCd')}
                        values={gradeOptions}
                        onChange={(e) => {
                            const value = parseInt(e.target.value);
                            changed({highestGradeCd: isNaN(value) ? undefined : value});
                        }} value={highestGradeCd?.toString()}></Dropdown>
                </FieldRow>
            </>
          }
            {/*As per the latest requirement, educationDeliveryMethodCds is valid for all org_sub_type cds*/}
            <>
                <FieldRow>
                    <>
                        <FieldLabel variant="body1" style={{marginBottom: '4px'}}>Education Delivery Method</FieldLabel>
                        <Checkboxes error={getError('educationDeliveryMethodCds')} value={deliveryMethod}
                                    readOnly={readOnly}
                                    options={TYPE_OPTIONS.map((val: any) => ({key: val.key, label: val.label}))}
                                    onChange={(values: number[]) => {
                                        changed({
                                            educationDeliveryMethodCds: values
                                        });
                                    }}/>
                    </>
                </FieldRow> 
            </>
        </Grid>
      </Grid>
    </Panel>
    <Panel>
      <Grid container spacing={5}>
        <Grid item xs={6}>
          <FieldRow><strong>Visiting Address</strong></FieldRow>
          <AddressFields readOnly={readOnly} errors={getFieldError('visitingAddress')} address={visitingAddress}
            showDiff={original ? true : false}
            original={original?.visitingAddress || {}}
            onUpdate={(address) => {
              changed({ visitingAddress: address });
            }} />
        </Grid>
        { type == 'ai' && 
        <>
        <Grid item xs={6}>
          <FieldRow><Checkbox disabled={readOnly} label={<strong>Shipping Address</strong>} style={{ display: 'inline-block' }} checked={shippingAddress ? true : false}
            onChange={() => {
              if (!shippingAddress) {
                changed({
                  shippingAddress: {
                    countryIsoCd: visitingAddress.countryIsoCd || 'US',
                    stateCd: visitingAddress.stateCd,
                    province: visitingAddress.province
                  }
                });
              } else {
                changed({ shippingAddress: undefined });
              }
            }} />
            {hasError('shippingAddress') &&
              <span className='cb-input-helper cb-validation-error'>{getError('shippingAddress')}</span>
            }
          </FieldRow>
          {shippingAddress ?
            <AddressFields additionalInfo={true} customLabels={{ label: 'Alternate Shipping Institution Name', contactName: 'Shipping Contact Name', contactPhone: 'Shipping Contact Phone' }} readOnly={readOnly} errors={getFieldError('shippingAddress')} address={initial?.shippingAddress}
              showDiff={original ? true : false}
              original={original?.shippingAddress || {}}
              onUpdate={(address) => {
                changed({ shippingAddress: address });
              }} /> :
            <div>{originalShipping &&
              <div><i className={getChangeColor('shippingAddress')}>Current:  {formatAddress(originalShipping)}</i></div>
            }</div>
          }
        </Grid>
        </>
         }
      </Grid>
    </Panel>
    {showProtected &&
      <Panel>
          <Grid container spacing={5}>
              <Grid item xs={6}>
                  <FieldRow>
                      <FieldLabel variant="body1">MDR PID</FieldLabel>
                      {(original && isChanged('mdrPid')) &&
                          <div><i className={getChangeColor('mdrPid')}>Current: {getCurrent('mdrPid') || '(empty)'}</i>
                          </div>
                      }
                      <Input disabled={readOnly} value={mdrPid} validation={hasError('mdrPid') ? 'error' : undefined}
                             validationMsg={getError('mdrPid')}
                             onChange={(e) => {
                                 changed({mdrPid: e.target.value || undefined});
                             }}
                      />
                      {(mdrPid || '').length > 0 &&
                          <FieldNote>Character Count: {(mdrPid || '').length}</FieldNote>
                      }
                  </FieldRow>
                  <FieldRow>
                      <FieldLabel variant="body1">NCES ID</FieldLabel>
                      {(original && isChanged('ncesId')) &&
                          <div><i className={getChangeColor('ncesId')}>Current: {getCurrent('ncesId') || '(empty)'}</i>
                          </div>
                      }
                      <Input disabled={readOnly} value={ncesId} validation={hasError('ncesId') ? 'error' : undefined}
                             validationMsg={getError('ncesId')}
                             onChange={(e) => {
                                 changed({ncesId: e.target.value || undefined});
                             }}
                      />
                      {(ncesId || '').length > 0 &&
                          <FieldNote>Character Count: {(ncesId || '').length}</FieldNote>
                      }
                  </FieldRow>
                  <FieldRow>
                      <FieldLabel variant="body1">IPEDS ID</FieldLabel>
                      {(original && isChanged('ipedsId')) &&
                          <div><i
                              className={getChangeColor('ipedsId')}>Current: {getCurrent('ipedsId') || '(empty)'}</i>
                          </div>
                      }
                      <Input disabled={readOnly} value={ipedsId} validation={hasError('ipedsId') ? 'error' : undefined}
                             validationMsg={getError('ipedsId')}
                             onChange={(e) => {
                                 changed({ipedsId: e.target.value || undefined});
                             }}
                      />
                      {(ipedsId || '').length > 0 &&
                          <FieldNote>Character Count: {(ipedsId || '').length}</FieldNote>
                      }
                  </FieldRow>
                  <FieldRow>
                      <FieldLabel variant="body1">Fee Waiver ID</FieldLabel>
                      {(original && isChanged('feeWaiverId')) &&
                          <div><i
                              className={getChangeColor('feeWaiverId')}>Current: {getCurrent('feeWaiverId') || '(empty)'}</i>
                          </div>
                      }
                      <Input disabled={readOnly} value={feeWaiverId}
                             validation={hasError('feeWaiverId') ? 'error' : undefined}
                             validationMsg={getError('feeWaiverId')}
                             onChange={(e) => {
                                 changed({feeWaiverId: e.target.value || undefined});
                             }}
                      />
                      {(feeWaiverId || '').length > 0 &&
                          <FieldNote>Character Count: {(feeWaiverId || '').length}</FieldNote>
                      }
                  </FieldRow>
              </Grid>
              <Grid item xs={6}>
                  <FieldRow>
                      <Typography variant="body1">Organization Comments</Typography>
                      {(readOnly && isChanged('orgCodeNotes')) &&
                          <div><i
                              className={getChangeColor('orgCodeNotes')}>Current: {getCurrent('orgCodeNotes') || '(empty)'}</i>
                          </div>
                      }
                      <TextArea
                          disabled={readOnly}
                          className={readOnly ? getChangeColor('orgCodeNotes') : ''}
                          validation={hasError('orgCodeNotes') ? 'error' : undefined}
                          validationMsg={getError('orgCodeNotes')}
                          onChange={(e) => {
                              changed({orgCodeNotes: e.target.value || undefined});
                          }} value={orgCodeNotes}/>
                      {(orgCodeNotes || '').length > 0 &&
                          <FieldNote>Character Count: {(orgCodeNotes || '').length}</FieldNote>
                      }
                  </FieldRow>
              </Grid>
          </Grid>
      </Panel>
    }
   
   
  </>;
};
